import { Routes } from '@angular/router';
import { needs } from '@core/config/workspace-route.util';

/**
 * Shared workspace routes — used across education / business / creator / learner
 * (nav visibility still filtered by permission / persona).
 *
 * People: business uses `/participants` (education overrides the index via
 * `EDUCATION_WORKSPACE_ROUTES` + `typeMatch('education')`).
 */
export const SHARED_WORKSPACE_ROUTES: Routes = [
  {
    path: 'assessment-engine',
    ...needs('assessment.edit', {
      ownerScope: 'workspace',
    }),
    loadComponent: () =>
      import(
        '../../pages/assessments/assessment-list/assessment-list.page'
      ).then((m) => m.AssessmentListPage),
  },
  {
    path: 'assessment-engine/templates',
    ...needs('assessment.edit', {
      ownerScope: 'workspace',
      minimalShell: true,
    }),
    loadComponent: () =>
      import(
        '../../pages/assessments/templates/assessment-templates.page'
      ).then((m) => m.AssessmentTemplatesPage),
  },
  {
    path: 'assessment-engine/library/transfer',
    ...needs('assessment.edit', {
      ownerScope: 'workspace',
      minimalShell: true,
    }),
    loadComponent: () =>
      import(
        '../../pages/assessments/library-transfer/assessment-library-transfer.page'
      ).then((m) => m.AssessmentLibraryTransferPage),
  },

  {
    path: 'assessment-engine/library',
    ...needs('assessment.edit', {
      ownerScope: 'workspace',
      minimalShell: true,
    }),
    loadComponent: () =>
      import(
        '../../pages/assessments/library/assessment-library.page'
      ).then((m) => m.AssessmentLibraryPage),
  },
  {
    path: 'assessment-engine/:assessmentGuid/releases',
    ...needs('assessment.edit', {
      ownerScope: 'workspace',
      minimalShell: true,
    }),
    loadComponent: () =>
      import(
        '../../pages/assessments/release-history/assessment-release-history.page'
      ).then((m) => m.AssessmentReleaseHistoryPage),
  },
  // Native Assessment Engine management is deliberately isolated from the
  // legacy Quiz `/assessments*` pages. The workspace shell supplies the selected
  // workspace; Assessment's resource layer remains the authorization authority.
  {
    path: 'assessment-engine/:assessmentGuid/manage',
    ...needs('assessment.edit', {
      ownerScope: 'workspace',
      minimalShell: true,
    }),
    loadComponent: () =>
      import(
        '../../pages/assessments/management/assessment-management.page'
      ).then((m) => m.AssessmentManagementPage),
  },
  {
    path: 'assessment-engine/:assessmentGuid/take',
    ...needs('assessment.view', {
      ownerScope: 'workspace',
      minimalShell: true,
    }),
    loadComponent: () =>
      import(
        '../../pages/assessments/attempt/assessment-attempt.page'
      ).then((m) => m.AssessmentAttemptPage),
  },
  {
    path: 'assessment-engine/:assessmentGuid/results',
    ...needs('assessment.grade', {
      ownerScope: 'workspace',
      minimalShell: true,
    }),
    loadComponent: () =>
      import(
        '../../pages/assessments/results/assessment-results.page'
      ).then((m) => m.AssessmentResultsPage),
  },
  {
    path: 'assessment-engine/campaigns',
    ...needs('campaign.view', {
      minimalShell: true,
    }),
    loadComponent: () =>
      import(
        '../../pages/assessments/campaigns/assessment-campaigns.page'
      ).then((m) => m.AssessmentCampaignsPage),
  },
  {
    path: 'assessment-engine/assigned',
    ...needs('assessment.view', {
      minimalShell: true,
    }),
    loadComponent: () =>
      import(
        '../../pages/assessments/campaigns/assigned-assessments.page'
      ).then((m) => m.AssignedAssessmentsPage),
  },
  // ── People (business participants directory + shared sub-pages) ────────────
  {
    path: 'participants',
    ...needs('participants.view'),
    loadComponent: () =>
      import(
        '../../pages/workspaces/shared/participants/participants.page'
      ).then((m) => m.ParticipantsDirectoryPage),
  },
  {
    path: 'participants/profile',
    ...needs('participants.view'),
    loadComponent: () =>
      import(
        '../../pages/workspaces/shared/participant-profile/participant-profile.page'
      ).then((m) => m.ParticipantProfilePage),
  },
  {
    path: 'participants/invitations',
    ...needs('participants.manage'),
    loadComponent: () =>
      import('../../pages/workspaces/shared/invitations/invitations.page').then(
        (m) => m.InvitationsPage,
      ),
  },
  {
    path: 'participants/import',
    ...needs('participants.manage'),
    loadComponent: () =>
      import(
        '../../pages/workspaces/shared/participants-import/participants-import.page'
      ).then((m) => m.ParticipantsImportPage),
  },
  {
    path: 'participants/followup',
    ...needs('participants.manage', { minimalShell: true }),
    loadComponent: () =>
      import(
        '../../pages/workspaces/shared/participant-followup/participant-followup.page'
      ).then((m) => m.ParticipantFollowupPage),
  },

  // ── Library ────────────────────────────────────────────────────────────────
  {
    path: 'library',
    ...needs('library.view', { ownerScope: 'workspace' }),
    loadComponent: () =>
      import('../../pages/assessments/library/assessment-library.page').then(
        (m) => m.AssessmentLibraryPage,
      ),
  },
  {
    path: 'library/questions',
    ...needs('library.view', { ownerScope: 'workspace' }),
    loadComponent: () =>
      import('../../pages/assessments/library/assessment-library.page').then(
        (m) => m.AssessmentLibraryPage,
      ),
  },
  {
    path: 'library/studio',
    ...needs('library.manage', { ownerScope: 'workspace' }),
    loadComponent: () =>
      import('../../pages/assessments/library/assessment-library.page').then(
        (m) => m.AssessmentLibraryPage,
      ),
  },
  {
    path: 'library/collections',
    ...needs('library.view', { ownerScope: 'workspace' }),
    loadComponent: () =>
      import('../../pages/assessments/library/assessment-library.page').then(
        (m) => m.AssessmentLibraryPage,
      ),
  },
  {
    path: 'library/imports',
    ...needs('library.manage', { ownerScope: 'workspace' }),
    loadComponent: () =>
      import(
        '../../pages/assessments/library-transfer/assessment-library-transfer.page'
      ).then((m) => m.AssessmentLibraryTransferPage),
  },
  {
    path: 'library/files',
    ...needs('library.view', { ownerScope: 'workspace' }),
    loadComponent: () =>
      import('../../pages/assessments/library/assessment-library.page').then(
        (m) => m.AssessmentLibraryPage,
      ),
  },
  {
    path: 'library/quality',
    ...needs('library.view', { ownerScope: 'workspace' }),
    loadComponent: () =>
      import('../../pages/assessments/library/assessment-library.page').then(
        (m) => m.AssessmentLibraryPage,
      ),
  },
  {
    path: 'library/question-types',
    ...needs('library.view', { ownerScope: 'workspace' }),
    loadComponent: () =>
      import('../../pages/assessments/library/assessment-library.page').then(
        (m) => m.AssessmentLibraryPage,
      ),
  },

  // ── Secondary ──────────────────────────────────────────────────────────────
  {
    path: 'messages',
    ...needs('messages.view', { minimalShell: true }),
    loadComponent: () =>
      import('../../pages/workspaces/shared/messages/messages.page').then(
        (m) => m.MessagesPage,
      ),
  },
  {
    path: 'calendar',
    ...needs('campaign.view', { ownerScope: 'workspace' }),
    loadComponent: () =>
      import('../../pages/assessments/campaigns/assessment-campaigns.page').then(
        (m) => m.AssessmentCampaignsPage,
      ),
  },
  {
    path: 'notifications',
    data: { minimalShell: true },
    loadComponent: () =>
      import(
        '../../pages/workspaces/shared/notifications/notifications.page'
      ).then((m) => m.NotificationsPage),
  },
  {
    path: 'activity',
    ...needs('activity.view'),
    loadComponent: () =>
      import('../../pages/workspaces/shared/activity/activity.page').then(
        (m) => m.ActivityPage,
      ),
  },
  {
    path: 'insights',
    ...needs('assessment.grade', {
      ownerScope: 'workspace',
      assessmentLanding: 'results',
    }),
    loadComponent: () =>
      import(
        '../../pages/assessments/assessment-list/assessment-list.page'
      ).then((m) => m.AssessmentListPage),
  },
  // Legacy alias — Results overview duplicated Insights; keep URL working.
  { path: 'results', pathMatch: 'full', redirectTo: 'insights' },

  {
    path: 'help',
    data: { minimalShell: true },
    loadComponent: () =>
      import('../../pages/workspaces/shared/help/help.page').then(
        (m) => m.HelpPage,
      ),
  },
];
